import {
  addDays,
  endOfMonth,
  endOfDay,
  format,
  startOfDay,
  startOfMonth,
} from 'date-fns';

export type DeadlinePreset =
  | 'all'
  | 'next7'
  | 'next14'
  | 'next30'
  | 'next60'
  | 'next90'
  | 'this_month'
  | 'next_month';

export const DEADLINE_PRESET_OPTIONS: { value: DeadlinePreset; label: string }[] = [
  { value: 'all', label: 'All deadlines' },
  { value: 'next7', label: 'Due in next 7 days' },
  { value: 'next14', label: 'Due in next 14 days' },
  { value: 'next30', label: 'Due in next 30 days' },
  { value: 'next60', label: 'Due in next 60 days' },
  { value: 'next90', label: 'Due in next 90 days' },
  { value: 'this_month', label: 'Due this month' },
  { value: 'next_month', label: 'Due next month' },
];

export function deadlinePresetLabel(preset: DeadlinePreset): string {
  return DEADLINE_PRESET_OPTIONS.find((o) => o.value === preset)?.label ?? 'All deadlines';
}

function presetDateRange(preset: DeadlinePreset): { start: Date; end: Date } | null {
  if (preset === 'all') return null;
  const today = startOfDay(new Date());
  switch (preset) {
    case 'next7':
      return { start: today, end: endOfDay(addDays(today, 7)) };
    case 'next14':
      return { start: today, end: endOfDay(addDays(today, 14)) };
    case 'next30':
      return { start: today, end: endOfDay(addDays(today, 30)) };
    case 'next60':
      return { start: today, end: endOfDay(addDays(today, 60)) };
    case 'next90':
      return { start: today, end: endOfDay(addDays(today, 90)) };
    case 'this_month':
      return { start: today, end: endOfMonth(today) };
    case 'next_month': {
      const nextMonthStart = startOfMonth(addDays(endOfMonth(today), 1));
      return { start: nextMonthStart, end: endOfMonth(nextMonthStart) };
    }
    default:
      return null;
  }
}

export function deadlinePresetToQueryFilters(
  preset: DeadlinePreset
): Record<string, string> {
  const range = presetDateRange(preset);
  if (!range) return {};
  return {
    deadline_gte: format(range.start, 'yyyy-MM-dd'),
    deadline_lte: format(range.end, 'yyyy-MM-dd'),
    include_expired: 'true',
  };
}

export function deadlinePresetMatchesDaysUntil(
  preset: DeadlinePreset,
  daysUntil: number | null | undefined
): boolean {
  if (preset === 'all') return true;
  if (daysUntil == null || daysUntil < 0) return false;
  const range = presetDateRange(preset);
  if (!range) return true;
  const deadlineDate = addDays(startOfDay(new Date()), daysUntil);
  return deadlineDate >= range.start && deadlineDate <= range.end;
}
